import {
  useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import { formatiereZahl } from './WertEinheit';
import { sichtbaresFenster } from './DataTable';
import {
  baumTaste, sichtliste, vorfahren,
  type BaumKnoten, type Baumzeile, type Kinderzustand,
} from './baum';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BAUM (TX3) - Gliederung mit Nachladen, Tastatur und Virtualisierung
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Für Netze und Gliederungen, die links stehen und rechts eine Tafel öffnen:
 * Rohrnetz (Gruppe → Klasse → Weg → Teilstrecke), Kanalnetz, Raumbuch,
 * Aussparungen. Muster: WAI-ARIA Authoring Practices „Tree View".
 *
 * - `role="tree"`, Knoten `role="treeitem"` mit `aria-level`, `aria-posinset`,
 *   `aria-setsize`, `aria-expanded`, `aria-selected`; EIN Knoten ist per Tab
 *   erreichbar (wandernder Fokus), die Pfeiltasten führen durch den Baum.
 *   Ein `<button>` darf nach „ARIA in HTML" die Rolle `treeitem` nicht
 *   tragen - deshalb ist der Knoten ein Element mit Rolle und eigener
 *   Tastatur, kein Knopf (Befund TX §10, Bedingung 1 der TH-Ausnahme).
 * - Nachladen je Knoten (`ladeKinder`): „wird geladen …", Fehler mit
 *   „Erneut versuchen", „keine Einträge" - je Knoten, nicht für den ganzen
 *   Baum. Die Zahl der Kinder (`anzahl`) steht vor dem Laden da.
 * - KEINE Kappung: Ab `virtualisierenAb` sichtbaren Zeilen stehen nur die im
 *   Rollbereich im Dokument, erreichbar sind alle.
 * - Die Blattebene ist Sache der Seite: `beiWahl` öffnet rechts eine
 *   `DataTable` (Bedingung 3 der TH-Ausnahme).
 */

export interface BaumProps<D> {
  /** Name des Baums - für Vorlesehilfen (`aria-label`). */
  name: string;
  knoten: BaumKnoten<D>[];
  /** Lädt die Kinder eines Knotens mit `hatKinder` und ohne `kinder`. */
  ladeKinder?: (knoten: BaumKnoten<D>) => Promise<BaumKnoten<D>[]>;
  /** Gewählter Knoten (Id). */
  gewaehlt?: string | null;
  /** Wahl eines Knotens: Klick, Eingabe oder Leertaste. `pfad` = Ids von der Wurzel. */
  beiWahl?: (knoten: BaumKnoten<D>, pfad: string[]) => void;
  /** Beim Öffnen aufgeklappte Knoten. Die Vorfahren von `gewaehlt` gehen immer auf. */
  anfangsOffen?: string[];

  laedt?: boolean;
  fehler?: unknown;
  beiErneutVersuchen?: () => void;
  /** Fachlicher Leerzustand (S1.4). */
  leer?: ReactNode;

  virtualisierenAb?: number;
  zeilenhoehe?: number;
  hoehe?: number;
  className?: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ZUSTAND - gemeinsam für Baum und BaumTabelle
   ═══════════════════════════════════════════════════════════════════════════ */

export function useBaumZustand<D>({
  knoten, ladeKinder, gewaehlt, anfangsOffen, beiWahl,
}: Pick<BaumProps<D>, 'knoten' | 'ladeKinder' | 'gewaehlt' | 'anfangsOffen' | 'beiWahl'>) {
  const [offen, setOffen] = useState<Set<string>>(() => new Set([
    ...(anfangsOffen ?? []),
    ...(gewaehlt ? (vorfahren(knoten, gewaehlt) ?? []) : []),
  ]));
  const [geladen, setGeladen] = useState<Map<string, Kinderzustand<D>>>(() => new Map());
  const [fokusId, setFokusId] = useState<string | null>(gewaehlt ?? null);
  /** Hochgezählt, wenn der Fokus nach dem Zeichnen ins Element gesetzt werden soll. */
  const [fokusAnfrage, setFokusAnfrage] = useState(0);
  const ladend = useRef(new Set<string>());

  const nachladen = useCallback((k: BaumKnoten<D>) => {
    if (!ladeKinder || k.kinder !== undefined || !k.hatKinder) return;
    if (ladend.current.has(k.id)) return;
    ladend.current.add(k.id);
    setGeladen((m) => new Map(m).set(k.id, { zustand: 'laedt' }));
    ladeKinder(k)
      .then((kinder) => setGeladen((m) => new Map(m).set(k.id, { zustand: 'geladen', kinder: kinder ?? [] })))
      .catch((fehler: unknown) => setGeladen((m) => new Map(m).set(k.id, { zustand: 'fehler', fehler })))
      .finally(() => { ladend.current.delete(k.id); });
  }, [ladeKinder]);

  const istOffen = useCallback((id: string) => offen.has(id), [offen]);
  const zeilen = useMemo(() => sichtliste(knoten, istOffen, geladen), [knoten, istOffen, geladen]);

  const oeffnen = (k: BaumKnoten<D>) => {
    setOffen((s) => new Set(s).add(k.id));
    const z = geladen.get(k.id);
    if (!z || z.zustand === 'fehler') nachladen(k);
  };
  const schliessen = (id: string) => setOffen((s) => {
    const n = new Set(s);
    n.delete(id);
    return n;
  });
  const umschalten = (k: BaumKnoten<D>) => (offen.has(k.id) ? schliessen(k.id) : oeffnen(k));
  const erneut = (id: string) => {
    const z = zeilen.find((x) => x.art === 'knoten' && x.knoten.id === id);
    if (z?.art === 'knoten') nachladen(z.knoten);
  };

  const waehle = (z: Extract<Baumzeile<D>, { art: 'knoten' }>) => {
    setFokusId(z.knoten.id);
    beiWahl?.(z.knoten, z.pfad);
  };

  // Der wandernde Fokus: der gewählte, sonst der erste sichtbare Knoten.
  const erster = zeilen.find((z) => z.art === 'knoten');
  const fokusSichtbar = zeilen.some((z) => z.art === 'knoten' && z.knoten.id === fokusId);
  const tabZiel = fokusSichtbar ? fokusId : (erster?.art === 'knoten' ? erster.knoten.id : null);

  const taste = (e: KeyboardEvent<HTMLElement>, index: number) => {
    if (e.target !== e.currentTarget) return;
    const h = baumTaste(zeilen, index, e.key);
    if (h.art === 'nichts') return;
    e.preventDefault();
    const z = zeilen[index];
    if (z?.art !== 'knoten') return;
    if (h.art === 'oeffnen') oeffnen(z.knoten);
    else if (h.art === 'schliessen') schliessen(h.id);
    else if (h.art === 'fokus') {
      const ziel = zeilen[h.index];
      if (ziel?.art === 'knoten') { setFokusId(ziel.knoten.id); setFokusAnfrage((n) => n + 1); }
    } else if (h.art === 'waehlen') {
      waehle(z);
      if (z.aufklappbar && !z.offen) oeffnen(z.knoten);
    }
  };

  return {
    zeilen, geladen, istOffen, oeffnen, schliessen, umschalten, erneut, waehle, taste,
    fokusId, tabZiel, fokusAnfrage,
  };
}

/** Druck: vor dem Drucken stehen alle sichtbaren Zeilen im Dokument (keine Virtualisierung). */
export function useDrucken(): boolean {
  const [drucken, setDrucken] = useState(false);
  useEffect(() => {
    const vor = () => flushSync(() => setDrucken(true));
    const nach = () => setDrucken(false);
    window.addEventListener('beforeprint', vor);
    window.addEventListener('afterprint', nach);
    return () => {
      window.removeEventListener('beforeprint', vor);
      window.removeEventListener('afterprint', nach);
    };
  }, []);
  return drucken;
}

/** Einrückung je Ebene - errechnete Geometrie (S1.8), als Merkmal an der Zeile. */
export const ebenenStil = (ebene: number): CSSProperties => ({ '--baum-ebene': ebene - 1 } as CSSProperties);

/** Fenster, Rollbereich und Fokus-in-den-Blick für Baum und BaumTabelle. */
export function useBaumFenster(anzahl: number, opt: {
  virtualisierenAb: number; zeilenhoehe: number; hoehe: number; drucken: boolean;
  fokusIndex: number; fokusAnfrage: number; elementId: (index: number) => string;
}) {
  const rollbereich = useRef<HTMLDivElement | null>(null);
  const [scrollOben, setScrollOben] = useState(0);
  const virtualisiert = anzahl > opt.virtualisierenAb && !opt.drucken;
  const fenster = virtualisiert
    ? sichtbaresFenster(anzahl, scrollOben, opt.hoehe, opt.zeilenhoehe, opt.virtualisierenAb)
    : { von: 0, bis: anzahl };
  const beiRollen = useCallback(() => {
    const el = rollbereich.current;
    if (el) setScrollOben(el.scrollTop);
  }, []);

  // Ein Knoten außerhalb des Fensters wird erst in den Blick gerollt, dann
  // fokussiert - je Tastendruck GENAU einmal: Rollt der Anwender danach selbst,
  // zieht der Baum ihn nicht zurück.
  const { fokusIndex, fokusAnfrage, zeilenhoehe, hoehe, elementId } = opt;
  const erledigt = useRef(0);
  useEffect(() => {
    if (fokusAnfrage === erledigt.current || fokusIndex < 0) return;
    const el = rollbereich.current;
    if (virtualisiert && el && (fokusIndex < fenster.von || fokusIndex >= fenster.bis)) {
      const oben = Math.max(0, fokusIndex * zeilenhoehe - hoehe / 2);
      el.scrollTop = oben;
      setScrollOben(oben);
      return;
    }
    erledigt.current = fokusAnfrage;
    document.getElementById(elementId(fokusIndex))?.focus();
  }, [fokusAnfrage, fokusIndex, fenster.von, fenster.bis, virtualisiert, zeilenhoehe, hoehe, elementId]);

  return { rollbereich, virtualisiert, fenster, beiRollen };
}

/** Die Zeile unter einem Knoten, deren Kinder laden, nicht geladen werden konnten oder leer sind. */
export function Kinderstatus({ zeile, erneut }: {
  zeile: Exclude<Baumzeile<unknown>, { art: 'knoten' }>;
  erneut: (eltern: string) => void;
}) {
  if (zeile.art === 'laedt') return <span className="m-baum-zustandstext">wird geladen …</span>;
  if (zeile.art === 'leer') return <span className="m-baum-zustandstext">keine Einträge</span>;
  return (
    <span className="m-baum-zustandstext m-baum-zustandstext-fehler">
      Konnte nicht geladen werden.
      {' '}
      <button type="button" className="m-knopf m-knopf-still" onClick={() => erneut(zeile.eltern)}>
        Erneut versuchen
      </button>
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BAUM
   ═══════════════════════════════════════════════════════════════════════════ */

export function Baum<D>({
  name, knoten, ladeKinder, gewaehlt, beiWahl, anfangsOffen,
  laedt, fehler, beiErneutVersuchen, leer,
  virtualisierenAb = 200, zeilenhoehe = 32, hoehe = 560, className,
}: BaumProps<D>) {
  const id = useId();
  const b = useBaumZustand({ knoten, ladeKinder, gewaehlt, anfangsOffen, beiWahl });
  const drucken = useDrucken();
  const elementId = (index: number) => {
    const z = b.zeilen[index];
    return z?.art === 'knoten' ? `${id}-k-${z.knoten.id}` : `${id}-z-${index}`;
  };
  const fokusIndex = b.zeilen.findIndex((z) => z.art === 'knoten' && z.knoten.id === b.fokusId);
  const f = useBaumFenster(b.zeilen.length, {
    virtualisierenAb, zeilenhoehe, hoehe, drucken, fokusIndex, fokusAnfrage: b.fokusAnfrage, elementId,
  });

  const rahmen = `m-baum-rahmen${className ? ` ${className}` : ''}`;
  if (fehler !== undefined && fehler !== null) {
    return (
      <div className={rahmen}>
        <ErrorState meldung={`${name}: Die Gliederung konnte nicht geladen werden.`} technisch={fehler}
          beiErneutVersuchen={beiErneutVersuchen} />
      </div>
    );
  }
  if (laedt) return <div className={rahmen}><LoadingState text={`${name} wird geladen …`} zeilen={6} spalten={1} /></div>;
  if (knoten.length === 0) {
    return (
      <div className={rahmen}>
        {leer ?? (
          <EmptyState titel="Noch nichts zu gliedern." klein
            erklaerung="Sobald Daten aufgenommen oder berechnet wurden, steht hier die Gliederung." />
        )}
      </div>
    );
  }

  const gezeigt = b.zeilen.slice(f.fenster.von, f.fenster.bis);

  return (
    <div className={rahmen}>
      <div
        className={`m-baum-koerper${f.virtualisiert ? ' m-tabelle-rollend' : ''}`}
        ref={f.rollbereich}
        onScroll={f.virtualisiert ? f.beiRollen : undefined}
        style={f.virtualisiert ? { maxHeight: hoehe } : undefined}
      >
        <div role="tree" aria-label={name} className="m-baum">
          {f.fenster.von > 0 && (
            <div aria-hidden="true" className="m-baum-luecke" style={{ height: f.fenster.von * zeilenhoehe }} />
          )}
          {gezeigt.map((z, i) => {
            const index = f.fenster.von + i;
            const hoeheStil = f.virtualisiert ? { height: zeilenhoehe } : {};
            if (z.art !== 'knoten') {
              return (
                <div key={`${z.eltern}-${z.art}`} className="m-baum-zustand"
                  style={{ ...ebenenStil(z.ebene), ...hoeheStil }}>
                  <Kinderstatus zeile={z} erneut={b.erneut} />
                </div>
              );
            }
            const k = z.knoten;
            const ladeZustand = b.geladen.get(k.id)?.zustand;
            return (
              <div
                key={k.id}
                id={elementId(index)}
                role="treeitem"
                aria-level={z.ebene}
                aria-posinset={z.position}
                aria-setsize={z.geschwister}
                aria-expanded={z.aufklappbar ? z.offen : undefined}
                aria-selected={gewaehlt === k.id}
                aria-busy={ladeZustand === 'laedt' ? true : undefined}
                tabIndex={b.tabZiel === k.id ? 0 : -1}
                className={`m-baum-knoten${gewaehlt === k.id ? ' m-baum-knoten-gewaehlt' : ''}`}
                style={{ ...ebenenStil(z.ebene), ...hoeheStil }}
                onClick={() => { b.waehle(z); if (z.aufklappbar) b.umschalten(k); }}
                onKeyDown={(e) => b.taste(e, index)}
              >
                <span className="m-baum-zeichen" aria-hidden="true">
                  {z.aufklappbar ? (z.offen ? '▾' : '▸') : ''}
                </span>
                <span className="m-baum-titel">{k.titel}</span>
                {k.anzahl !== undefined && (
                  <span className="m-baum-anzahl">
                    {formatiereZahl(k.anzahl)}
                    <span className="m-nur-vorlesen"> Einträge</span>
                  </span>
                )}
                {k.hinweis && <span className="m-baum-hinweis">{k.hinweis}</span>}
                {k.marke && <span className="m-baum-marke">{k.marke}</span>}
                {k.wert !== undefined && <span className="m-baum-wert">{k.wert}</span>}
              </div>
            );
          })}
          {f.fenster.bis < b.zeilen.length && (
            <div aria-hidden="true" className="m-baum-luecke"
              style={{ height: (b.zeilen.length - f.fenster.bis) * zeilenhoehe }} />
          )}
        </div>
      </div>
      {f.virtualisiert && (
        <p className="m-tabelle-fuss">
          {formatiereZahl(b.zeilen.length)} Zeilen; dargestellt werden nur die sichtbaren.
          Rollen Sie im Baum oder gehen Sie mit den Pfeiltasten, um die übrigen zu sehen.
        </p>
      )}
    </div>
  );
}
